import { useEffect, useMemo, useRef } from 'react'
import { WEEKDAYS, iso, monthGrid, sameDay, sameMonth, today } from './calendarDates'

/* The month grid DatePicker and DateRangeField share; the arithmetic is in calendarDates.js. */

/**
 * One month. Stateless: the parent owns the value, the view and the focused day, and
 * says per day what it is through `dayState`.
 *
 * ONE TAB STOP. Only the focused day is in the tab order (a roving tabindex) — a grid of
 * 42 buttons each taking a Tab is a wall to walk through; the arrows move inside it.
 *
 * @param {Date} view  any day in the month to draw
 * @param {Date} [focusDay]  the day that holds the tab stop and, while `autoFocus`, the focus
 * @param {(day: Date) => {selected?: boolean, start?: boolean, end?: boolean, inRange?: boolean, blocked?: boolean}} dayState
 * @param {boolean} [hideOutside]  leave the other months' days blank — two months side
 *   by side would otherwise each draw the other's edge
 */
export function CalendarMonth({ view, focusDay, autoFocus, dayState, onPick, onHover, onKeyDown, hideOutside = false }) {
  const gridRef = useRef(null)
  const days = useMemo(() => monthGrid(view), [view])
  const now = useMemo(() => today(), [])
  const tabDay = focusDay && sameMonth(focusDay, view) ? focusDay : null
  /* By value: a fresh Date each render must not re-pull the focus. */
  const tabKey = tabDay ? iso(tabDay) : ''

  useEffect(() => {
    if (!autoFocus || !tabKey) return
    gridRef.current?.querySelector(`[data-day="${tabKey}"]`)?.focus()
  }, [autoFocus, tabKey])

  return (
    <div className="calendar">
      <div className="calendar__weekdays" aria-hidden="true">
        {WEEKDAYS.map((label) => (
          <span key={label} className="calendar__weekday">
            {label}
          </span>
        ))}
      </div>

      <div className="calendar__grid" ref={gridRef} onKeyDown={onKeyDown} onPointerLeave={() => onHover?.(null)}>
        {days.map(({ day, outside }, index) => {
          if (outside && hideOutside) return <span key={index} className="calendar__blank" />
          const state = dayState(day)
          const isToday = sameDay(day, now)
          const tabbable = tabDay ? sameDay(day, tabDay) : !outside && day.getDate() === 1
          return (
            <button
              key={index}
              type="button"
              data-day={iso(day)}
              tabIndex={tabbable ? 0 : -1}
              className={[
                'calendar__day',
                outside && 'is-outside',
                isToday && 'is-today',
                state.selected && 'is-selected',
                state.start && 'is-start',
                state.end && 'is-end',
                state.inRange && 'is-in-range',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={state.blocked}
              aria-current={isToday ? 'date' : undefined}
              aria-pressed={Boolean(state.selected || state.start || state.end)}
              onClick={() => onPick(day)}
              onPointerEnter={() => onHover?.(day)}
            >
              {day.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
